/** @format */

import {useEffect, useState, useRef} from 'react';

export default function useFetch(
    url,
    updateData,
    debounceDelay = 0,
    cacheConfig = {enableCache: false, cacheTimeout: '+1 d'}
) {
    const [isLoading, setIsLoading] = useState(false);
    const abortControllerRef = useRef(null);
    const debounceTimeoutRef = useRef(null);

    function getCacheKey(url) {
        return `cache_${url}`;
    }

    function isCacheValid(cacheTime, cacheTimeout) {
        const now = Date.now();
        const timeout = new Date(cacheTime).getTime() + parseCacheTimeout(cacheTimeout);
        return now < timeout;
    }

    function parseCacheTimeout(timeout) {
        const timeValue = parseInt(timeout.slice(1), 10);
        const timeUnit = timeout.slice(-1);
        switch (timeUnit) {
            case 'd':
                return timeValue * 24 * 60 * 60 * 1000;
            case 'h':
                return timeValue * 60 * 60 * 1000;
            case 'm':
                return timeValue * 60 * 1000;
            case 's':
                return timeValue * 1000;
            default:
                return 0;
        }
    }

    async function makeRequest(options = {}) {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            setIsLoading(true);
            const fetchOptions = {
                ...options,
                ...{
                    signal: abortController.signal,
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    }
                }
            };
            const response = await fetch(url, fetchOptions);
            const data = await response.json();
            setIsLoading(false);
            updateData(data);

            if (cacheConfig.enableCache) {
                localStorage.setItem(getCacheKey(url), JSON.stringify({data, time: Date.now()}));
            }
        } catch (e) {
            if (e.name !== 'AbortError') {
                console.error(e);
            } else {
                console.log('Fetch aborted');
            }
        }
    }

    async function fetchData(options = {}) {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }

        if (debounceDelay > 0) {
            debounceTimeoutRef.current = setTimeout(makeRequest.bind(this, options), debounceDelay);
        } else {
            await makeRequest(options);
        }
    }

    function abortFetchData() {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    }

    useEffect(() => {
        if (cacheConfig.enableCache) {
            const cachedData = localStorage.getItem(getCacheKey(url));
            if (cachedData) {
                const {data, time} = JSON.parse(cachedData);
                if (isCacheValid(time, cacheConfig.cacheTimeout)) {
                    updateData(data);
                    return;
                }
            }
        }
        fetchData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [url]);

    return {fetchData, abortFetchData, isLoading};
}
